import { navigateTo } from '../router';
import { listRefs, createRef, type GameMap, type GameMod, type MapType, type TeamFormat, MAP_TYPES, TEAM_FORMATS } from '../api/game-refs';
import { apiRequest, ApiRequestError } from '../api';
import { listMods, type ModSetting, type ModSettingPreset } from '../api/mods';
import { buildModSettingsBox, presetToRoll, rollModSettings, toggleModSetting, type ModSettingRoll } from '../ui/mod-settings';
import { applyLocalExclusions, countModExclusions, openModExclusionsModal } from '../ui/mod-exclusions';
import { state } from '../state';
import { t } from '../i18n';
import { hideMapPreview, showMapPreview } from '../ui/map-preview';
import { buildLinkCopyButton, copyWithToast } from '../ui/copy';
import { LEAGUE_DYNAMIC_TYPE, isMapStyleType, leagueMapIds, leagueMapTypes, mapTeamFormats, mapTypesWithLeague, teamFormatLabel } from '../ui/map-presets';
import { buildPoolPicker, mapToPoolItem, modToPoolItem, type PoolItem, type PoolKind as Kind } from '../ui/pool-picker';
import { buildOverlayToggle, showOverlayRoll, startOverlayRoll, type MapRollOutput } from '../ui/overlay-output';

// ============================================================
// Инструмент «Выбор карты и режима»: два барабана (слот-машина)
// крутятся и останавливаются на случайной карте и моде.
// Исключённые из розыгрыша id хранятся локально (localStorage),
// поэтому новые карты/моды по умолчанию включены.
// Пресеты (/api/map-presets): общие (правят модераторы/ghost) и личные
// (залогиненные пользователи). Пресет хранит ВКЛЮЧЁННЫЕ id.
// После розыгрыша настройки выпавшего мода рандомизируются
// (/api/mods): параметры с randomize=false остаются на дефолте,
// остальные получают случайное значение; пресеты можно применить вручную.
// ============================================================

interface MapPreset {
    id: number;
    name: string;
    shared: boolean;
    map_ids: number[];
    mod_ids: number[];
}

interface Excluded {
    maps: number[];
    mods: number[];
}

const STORAGE_KEY = 'tools.random.excluded';
/** «Пропускать дисбалансные карты» — по умолчанию включено. */
const SKIP_UNBALANCED_KEY = 'tools.random.skip_unbalanced';
/** Высота строки барабана — должна совпадать с --rr-item-h в tools.css. */
const ITEM_H = 68;

let pools: Record<Kind, PoolItem[]> = { maps: [], mods: [] };
let excluded: Excluded = { maps: [], mods: [] };
/** Пропускать карты с пометкой «дисбалансная» (по умолчанию да). */
let skipUnbalanced = true;
/** Последний выпавший id — чтобы не выпадало одно и то же два раза подряд. */
const lastPick: Record<Kind, number | null> = { maps: null, mods: null };
let spinning = false;

let presets: MapPreset[] = [];
/** Может ли пользователь править общие пресеты и добавлять карты (moderator/ghost/admin). */
let canEditShared = false;
/** Последний применённый сохранённый пресет (для «Сохранить изменения» и пометки «изменён»). */
let activePresetId: number | null = null;
/** Быстрый выбор по пулу: списки лиги / тип карты + число игроков / формат командных карт. */
type DynamicPreset =
    | { kind: 'league' }
    | { kind: 'type'; type: MapType; count: number | null }
    | { kind: 'format'; format: TeamFormat };
let activeDynamic: DynamicPreset | null = null;
const PRESET_KEY = 'tools.random.preset';
/** Флаг «пользователь уже выбирал пресет» — гасит подсказку-подсветку раздела. */
const PRESET_ACK_KEY = 'tools.random.preset.ack';
let rootEl: HTMLElement | null = null;

/** Настройки и пресеты модов (из публичного /api/mods) — для рандомизации после розыгрыша. */
let modSettings = new Map<number, ModSetting>();
/** Серверные (общие) настройки — к ним возвращает «Сбросить на умолчания». */
let modSettingsBase = new Map<number, ModSetting>();
let modPresets = new Map<number, ModSettingPreset[]>();
/** Результат рандомизации настроек выпавшего мода. */
let settingsRoll: ModSettingRoll | null = null;
/** Слот под блок настроек в текущем DOM (пересоздаётся при render). */
let settingsSlot: HTMLElement | null = null;
/** Слот под панель результата (картинка + сведения) — пересоздаётся при render. */
let pickPanelEl: HTMLElement | null = null;
/** Обновляет панель пресетов (пометку «изменён», кнопки) после ручной правки пула. */
let refreshPresetsPanel: () => void = () => {};

function loadExcluded(): Excluded {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return { maps: [], mods: [] };
        const d = JSON.parse(raw) as Partial<Excluded>;
        return {
            maps: Array.isArray(d.maps) ? d.maps.filter(Number.isFinite) : [],
            mods: Array.isArray(d.mods) ? d.mods.filter(Number.isFinite) : [],
        };
    } catch {
        return { maps: [], mods: [] };
    }
}

function saveExcluded(): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(excluded));
    } catch {
        /* localStorage недоступен — работаем без сохранения */
    }
}

/** Флаг «пропускать дисбалансные»: по умолчанию включён. */
function loadSkipUnbalanced(): boolean {
    try {
        return localStorage.getItem(SKIP_UNBALANCED_KEY) !== '0';
    } catch {
        return true;
    }
}

function saveSkipUnbalanced(): void {
    try {
        localStorage.setItem(SKIP_UNBALANCED_KEY, skipUnbalanced ? '1' : '0');
    } catch {
        /* localStorage недоступен — работаем без сохранения */
    }
}

/** Карта участвует в розыгрыше с учётом пометки «дисбалансная». */
const isSkippedUnbalanced = (it: PoolItem): boolean =>
    Boolean(it.unbalanced) && skipUnbalanced;

const enabledOf = (kind: Kind): PoolItem[] =>
    pools[kind].filter(
        (it) => !excluded[kind].includes(it.id) && !(kind === 'maps' && isSkippedUnbalanced(it))
    );

/**
 * Карты, подходящие под быстрый выбор: объединение общих пресетов «FFA Лига»,
 * тип карты + число игроков либо формат командной карты (2 на 2, 3 на 3, …).
 */
const dynamicMapsOf = (preset: DynamicPreset): PoolItem[] => {
    if (preset.kind === 'league') {
        const ids = leagueMapIds(presets);
        return pools.maps.filter((it) => ids.has(it.id));
    }
    if (preset.kind === 'format') {
        return pools.maps.filter((it) => (it.teamFormats ?? []).includes(preset.format));
    }
    return pools.maps.filter(
        (it) => (it.mapTypes ?? []).includes(preset.type) && (preset.count === null || it.playerCount === preset.count)
    );
};

/** Значение выбора динамического пресета: `d:league`, `d:<type>:<count|all>` или `d:fmt:<format>`. */
const dynamicValue = (preset: DynamicPreset): string => {
    if (preset.kind === 'league') return `d:${LEAGUE_DYNAMIC_TYPE}`;
    if (preset.kind === 'format') return `d:fmt:${preset.format}`;
    return `d:${preset.type}:${preset.count ?? 'all'}`;
};

/** Разбор сохранённого значения динамического пресета (null — не распознали). */
const parseDynamicValue = (raw: string): DynamicPreset | null => {
    if (!raw.startsWith('d:')) return null;
    const [, type, extra] = raw.split(':');
    if (type === LEAGUE_DYNAMIC_TYPE) return { kind: 'league' };
    if (type === 'fmt') {
        return (TEAM_FORMATS as readonly string[]).includes(extra ?? '')
            ? { kind: 'format', format: extra as TeamFormat }
            : null;
    }
    if (MAP_TYPES.includes(type as MapType)) {
        return { kind: 'type', type: type as MapType, count: extra === 'all' ? null : Number(extra) };
    }
    return null;
};

/** Форматы командных карт, которые реально есть в текущем пуле. */
const presentTeamFormats = (): TeamFormat[] =>
    TEAM_FORMATS.filter((format) => pools.maps.some((it) => (it.teamFormats ?? []).includes(format)));

export async function mountRandom(): Promise<void> {
    document.querySelectorAll<HTMLElement>('.screen').forEach((el) => {
        el.classList.toggle('hidden', el.id !== 'screen-random');
    });

    const backBtn = document.getElementById('btn-back-tools-random');
    if (backBtn) {
        const fresh = backBtn.cloneNode(true) as HTMLElement;
        backBtn.replaceWith(fresh);
        fresh.addEventListener('click', () => navigateTo('/tools', true));
    }

    excluded = loadExcluded();
    skipUnbalanced = loadSkipUnbalanced();
    spinning = false;
    try {
        const raw = localStorage.getItem(PRESET_KEY) ?? '';
        activeDynamic = parseDynamicValue(raw);
        activePresetId = activeDynamic ? null : Number(raw) || null;
    } catch {
        activePresetId = null;
        activeDynamic = null;
    }

    const root = document.getElementById('random-root');
    if (!root) return;
    rootEl = root;
    root.textContent = t('common.loading');

    try {
        const [maps, mods] = await Promise.all([
            listRefs<GameMap>('maps'),
            listRefs<GameMod>('mods'),
            loadPresets(),
            loadModSettings(),
        ]);
        pools = {
            // Мягко удалённые карты в розыгрыше не участвуют
            maps: maps.filter((m) => !m.deleted_at).map(mapToPoolItem),
            mods: mods.map(modToPoolItem),
        };
    } catch (err) {
        console.error('[tools/random] failed to load refs:', err);
        root.textContent = t('tools.random_load_error');
        return;
    }

    if (!presets.some((p) => p.id === activePresetId)) activePresetId = null;
    // Динамический пресет актуален только если по нему есть карты
    if (activeDynamic && dynamicMapsOf(activeDynamic).length === 0) {
        activeDynamic = null;
    }
    render(root);
}

export function unmountRandom(): void {
    spinning = false;
    rootEl = null;
    settingsSlot = null;
    pickPanelEl = null;
    hideMapPreview();
}

/** Грузит пресеты; ошибка не фатальна — инструмент работает и без них. */
async function loadPresets(): Promise<void> {
    try {
        const res = await apiRequest<{ presets: MapPreset[]; can_edit_shared: boolean }>('/api/map-presets', {
            token: state.token,
        });
        presets = res.presets;
        canEditShared = res.can_edit_shared;
    } catch (err) {
        console.error('[tools/random] failed to load presets:', err);
        presets = [];
        canEditShared = Boolean(state.user?.is_moderator || state.user?.is_ghost || state.user?.is_admin);
    }
}

/** Грузит настройки модов; ошибка не фатальна — розыгрыш работает и без них. */
async function loadModSettings(): Promise<void> {
    try {
        const mods = await listMods(state.token);
        const base = new Map<number, ModSetting>();
        const effective = new Map<number, ModSetting>();
        const presets = new Map<number, ModSettingPreset[]>();
        for (const m of mods) {
            if (m.settings && m.settings.params.length > 0) {
                base.set(m.id, m.settings);
                // Личные исключения пользователя накладываются на общие
                effective.set(m.id, applyLocalExclusions(m.id, m.settings));
            }
            if (m.presets.length > 0) presets.set(m.id, m.presets);
        }
        modSettingsBase = base;
        modSettings = effective;
        modPresets = presets;
    } catch (err) {
        console.error('[tools/random] failed to load mod settings:', err);
        modSettingsBase = new Map();
        modSettings = new Map();
        modPresets = new Map();
    }
}

/** Может ли пользователь править исключения настроек (moderator / ghost / админ). */
function canEditMods(): boolean {
    return (
        canEditShared ||
        Boolean(state.user?.is_moderator || state.user?.is_ghost || state.user?.is_admin)
    );
}

/**
 * Окно исключений для выпавшего мода. У модератора сохраняется на сервере
 * (общие настройки), у обычного пользователя — локально в браузере.
 * После сохранения значения сразу перерандомизируются по новым правилам.
 */
async function editModExclusions(modId: number, rollPresets: ModSettingPreset[]): Promise<void> {
    const baseSetting = modSettingsBase.get(modId) ?? modSettings.get(modId);
    if (!baseSetting) return;
    const modName = pools.mods.find((m) => m.id === modId)?.name ?? '';
    const result = await openModExclusionsModal(
        { id: modId, name: modName, settings: baseSetting, presets: rollPresets },
        { canEditShared: canEditMods(), token: state.token }
    );
    if (!result) return;

    // Общие настройки обновляем только при сохранении модератором
    if (result.shared) modSettingsBase.set(modId, result.settings);
    if (result.settings.params.length > 0) modSettings.set(modId, result.settings);
    else modSettings.delete(modId);
    settingsRoll = rollModSettings(settingsRoll, modId, modSettings, rollPresets);
    drawSettingsResult();
}

/** Перерисовывает блок настроек выпавшего мода (или прячет слот). */
function drawSettingsResult(): void {
    if (!settingsSlot) return;
    settingsSlot.innerHTML = '';
    settingsSlot.classList.add('hidden');

    const roll = settingsRoll;
    if (!roll || !modSettings.has(roll.modId)) return;

    const modName = pools.mods.find((m) => m.id === roll.modId)?.name ?? '';
    const rollPresets = modPresets.get(roll.modId) ?? [];
    settingsSlot.appendChild(
        buildModSettingsBox(roll, modName, rollPresets, {
            // Окно исключений доступно всем: модератор сохраняет общие,
            // пользователь — личные (локально)
            onExclusions: () => void editModExclusions(roll.modId, rollPresets),
            exclusions: countModExclusions(modSettings.get(roll.modId)),
            onReroll: () => {
                if (!settingsRoll) return;
                // Режим («случайные» / «из пресета») сохраняется между перерозыгрышами
                settingsRoll = rollModSettings(settingsRoll, settingsRoll.modId, modSettings, rollPresets);
                drawSettingsResult();
                pushOverlayResult();
            },
            onPreset: (preset) => {
                if (!settingsRoll) return;
                settingsRoll = presetToRoll(settingsRoll.modId, preset, modSettings) ?? settingsRoll;
                drawSettingsResult();
                pushOverlayResult();
            },
            onToggle: (name) => {
                if (!settingsRoll) return;
                settingsRoll = toggleModSetting(settingsRoll, name, modSettings) ?? settingsRoll;
                drawSettingsResult();
                pushOverlayResult();
            },
            onMode: (mode) => {
                if (!settingsRoll) return;
                settingsRoll = rollModSettings(settingsRoll, settingsRoll.modId, modSettings, rollPresets, mode);
                drawSettingsResult();
                pushOverlayResult();
            },
        })
    );
    settingsSlot.classList.remove('hidden');
}

/**
 * Панель результата под барабанами: крупная картинка выпавшей карты слева,
 * справа — название карты, мод, число игроков, типы и пресеты с этой картой.
 * animate=true — проиграть анимацию появления после розыгрыша.
 */
function drawPickPanel(animate: boolean): void {
    const el = pickPanelEl;
    if (!el) return;
    hideMapPreview();
    el.classList.remove('is-visible', 'is-animated');
    el.innerHTML = '';

    const id = lastPick.maps;
    const map = id == null ? undefined : enabledOf('maps').find((it) => it.id === id);
    if (!map) return;

    // --- Слева: картинка карты (или заглушка, если её нет) ---
    const imageBox = document.createElement('div');
    imageBox.className = 'rr-preview-image';
    if (map.imageUrl) {
        const img = document.createElement('img');
        img.src = map.imageUrl;
        img.alt = map.name;
        imageBox.appendChild(img);
        // Увеличенный предпросмотр при наведении (как в пуле карт)
        imageBox.classList.add('is-zoomable');
        imageBox.addEventListener('mouseenter', () => showMapPreview(map.imageUrl!, imageBox));
        imageBox.addEventListener('mouseleave', hideMapPreview);
    } else {
        imageBox.classList.add('is-empty');
    }
    el.appendChild(imageBox);

    // --- Справа: сведения о выпавшем варианте ---
    const info = document.createElement('div');
    info.className = 'rr-preview-info';

    const name = document.createElement('div');
    name.className = 'rr-preview-map-name is-copyable';
    name.textContent = map.name;
    name.title = t('maps.copy_name_hint');
    // Клик по названию — копирование (как на странице «Карты»)
    name.addEventListener('click', () => void copyWithToast(map.name));
    info.appendChild(name);

    if (map.title) {
        const alt = document.createElement('div');
        alt.className = 'rr-preview-map-alt';
        alt.textContent = map.title;
        info.appendChild(alt);
    }

    // Ссылка карты: клик копирует её в буфер (как в разделе «Карты»)
    if (map.link) info.appendChild(buildLinkCopyButton(map.link, 'map-card-link rr-preview-link', true));

    const mod = enabledOf('mods').find((it) => it.id === lastPick.mods);
    if (mod) {
        const modLine = document.createElement('div');
        modLine.className = 'rr-preview-mod-name is-copyable';
        modLine.title = t('maps.copy_name_hint');
        const label = document.createElement('span');
        label.className = 'rr-preview-label';
        label.textContent = `${t('tools.random_mod')}: `;
        modLine.append(label, document.createTextNode(mod.name));
        // Клик по названию мода — копирование
        modLine.addEventListener('click', () => void copyWithToast(mod.name));
        info.appendChild(modLine);
    }

    // Число игроков и типы карты
    const badges = document.createElement('div');
    badges.className = 'rr-preview-badges';
    if (map.unbalanced) {
        const b = document.createElement('span');
        b.className = 'rr-map-badge rr-map-badge--unbalanced';
        b.textContent = t('tools.random_unbalanced_badge');
        b.title = t('maps.unbalanced_hint');
        badges.appendChild(b);
    }
    if (map.playerCount != null) {
        const b = document.createElement('span');
        b.className = 'rr-map-badge';
        b.textContent = t('tools.random_players', { n: map.playerCount });
        badges.appendChild(b);
    }
    for (const type of mapTypesWithLeague(map, leagueMapTypes(presets))) {
        const b = document.createElement('span');
        b.className = 'rr-map-badge rr-map-badge--type' + (isMapStyleType(type) ? ' rr-map-badge--style' : '');
        b.textContent = t(`map_type.${type}` as any);
        badges.appendChild(b);
    }
    // Формат командной карты выпавшего варианта
    for (const format of map.teamFormats ?? []) {
        const b = document.createElement('span');
        b.className = 'rr-map-badge rr-map-badge--format';
        b.textContent = teamFormatLabel(format);
        badges.appendChild(b);
    }
    if (badges.childElementCount > 0) info.appendChild(badges);

    el.appendChild(info);
    el.classList.add('is-visible');
    if (animate) {
        void el.offsetWidth; // reflow — чтобы анимация перезапускалась при новом розыгрыше
        el.classList.add('is-animated');
    }
}

/** Прячет панель результата (во время прокрута барабанов). */
function hidePickPanel(): void {
    hideMapPreview();
    if (!pickPanelEl) return;
    pickPanelEl.classList.remove('is-visible', 'is-animated');
    pickPanelEl.innerHTML = '';
}

/** Данные для виджета OBS-оверлея: выпавшая карта, мод и настройки мода. */
function buildOverlayRoll(): MapRollOutput | null {
    const map = pools.maps.find((it) => it.id === lastPick.maps);
    if (!map) return null;
    const mod = pools.mods.find((it) => it.id === lastPick.mods);
    return {
        mapName: map.name,
        mapAlt: map.title ?? null,
        mapImage: map.imageUrl ?? null,
        modName: mod?.name ?? null,
        playerCount: map.playerCount ?? null,
        types: mapTypesWithLeague(map, leagueMapTypes(presets)),
        // На оверлей — только включённые (рандомизированные) параметры
        settings: (settingsRoll?.params ?? [])
            .filter((p) => p.enabled)
            .map((p) => ({ name: p.name, value: p.value })),
    };
}

/** Отправляет текущий результат на оверлей (если галочка включена). */
function pushOverlayResult(): void {
    const data = buildOverlayRoll();
    if (data) void showOverlayRoll(data);
}

/**
 * Плавно подводит страницу к выпавшему варианту (панель результата + настройки),
 * чтобы результат сразу был виден целиком. Если он уже в кадре — не дёргаем.
 */
function revealResult(): void {
    const panel = pickPanelEl;
    if (!panel || !panel.classList.contains('is-visible')) return;

    const vh = window.innerHeight || document.documentElement.clientHeight;
    const pad = 16;
    const box = panel.getBoundingClientRect();
    // Настройки идут сразу под панелью — по возможности показываем и их
    const settings = settingsSlot && !settingsSlot.classList.contains('hidden') ? settingsSlot : null;
    const bottom = (settings ?? panel).getBoundingClientRect().bottom;
    const height = bottom - box.top;

    // Блок уже целиком в кадре — не трогаем скролл
    if (box.top >= pad && bottom <= vh - pad) return;

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    // Помещается в экран — центрируем, иначе выравниваем по верху с отступом
    const top =
        height + pad * 2 <= vh
            ? window.scrollY + box.top - Math.max(pad, (vh - height) / 2)
            : window.scrollY + box.top - pad;
    window.scrollTo({ top: Math.max(0, top), behavior: reduced ? 'auto' : 'smooth' });
}

function setActivePreset(id: number | null): void {
    activePresetId = id;
    activeDynamic = null;
    try {
        if (id === null) localStorage.removeItem(PRESET_KEY);
        else localStorage.setItem(PRESET_KEY, String(id));
    } catch {
        /* без localStorage */
    }
}

/** Запоминает выбранный быстрый выбор (список лиги / тип+число игроков / формат). */
function setActiveDynamic(preset: DynamicPreset | null): void {
    activeDynamic = preset;
    activePresetId = null;
    try {
        if (preset) localStorage.setItem(PRESET_KEY, dynamicValue(preset));
        else localStorage.removeItem(PRESET_KEY);
    } catch {
        /* без localStorage */
    }
}

function rerender(): void {
    if (!rootEl || spinning) return;
    // Полная перерисовка схлопывает страницу и браузер сбрасывает скролл.
    // Держимся за пул карт (он сразу под рандомизатором, пресеты — в самом низу):
    // меняется именно состав пула, панель результата выше может появиться/скрыться.
    const before = rootEl.querySelector<HTMLElement>('.rr-pools')?.getBoundingClientRect().top ?? null;
    const y = window.scrollY;
    render(rootEl);
    const after = rootEl.querySelector<HTMLElement>('.rr-pools')?.getBoundingClientRect().top ?? null;
    const target =
        before !== null && after !== null ? Math.max(0, window.scrollY + after - before) : y;
    window.scrollTo({ top: target, behavior: 'auto' });
    requestAnimationFrame(() => window.scrollTo({ top: target, behavior: 'auto' }));
}

function render(root: HTMLElement): void {
    root.innerHTML = '';

    // --- Барабаны ---
    const machine = document.createElement('div');
    machine.className = 'rr-machine';

    const reels = document.createElement('div');
    reels.className = 'rr-reels';
    const mapReel = buildReel(t('tools.random_map'), 'maps');
    const modReel = buildReel(t('tools.random_mod'), 'mods');
    reels.append(mapReel.el, modReel.el);

    const spinBtn = document.createElement('button');
    spinBtn.type = 'button';
    spinBtn.className = 'btn-primary rr-spin';
    spinBtn.textContent = t('tools.random_spin');
    spinBtn.addEventListener('click', () => {
        if (spinning) return;
        spinning = true;
        spinBtn.disabled = true;
        machine.classList.remove('is-done');
        // Режим настроек («случайные» / «из пресета») переживает новый розыгрыш
        const keepMode = settingsRoll?.mode ?? 'random';
        // Старые настройки и панель результата во время прокрута не показываются
        settingsRoll = null;
        drawSettingsResult();
        hidePickPanel();
        // OBS-оверлей: крутит свои барабаны, пока крутятся барабаны сайта
        void startOverlayRoll({
            maps: enabledOf('maps').map((it) => it.name),
            mods: enabledOf('mods').map((it) => it.name),
        });
        // Мод останавливается позже карты — эффект «по очереди»
        void Promise.all([mapReel.spin(2200), modReel.spin(3000)]).then(() => {
            spinning = false;
            spinBtn.disabled = false;
            machine.classList.add('is-done');
            // После розыгрыша рандомизируем настройки выпавшего мода
            const modId = lastPick.mods;
            settingsRoll = rollModSettings(
                null,
                modId,
                modSettings,
                modId == null ? [] : modPresets.get(modId) ?? [],
                keepMode
            );
            drawSettingsResult();
            // ...и показываем панель выпавшего варианта
            drawPickPanel(true);
            // ...и тот же результат — на OBS-оверлей
            pushOverlayResult();
            // Подводим страницу к результату (плавно, только если он не в кадре)
            requestAnimationFrame(revealResult);
        });
    });

    machine.append(reels, spinBtn);

    // --- Галочка «Выводить на оверлей OBS» (по умолчанию включена) ---
    machine.appendChild(
        buildOverlayToggle((on) => {
            if (on && lastPick.maps != null) pushOverlayResult();
        })
    );

    // --- Панель результата: картинка карты + сведения (см. drawPickPanel) ---
    const pickPanel = document.createElement('div');
    pickPanel.className = 'rr-preview';
    pickPanelEl = pickPanel;

    // --- Пулы карт и модов ---
    const pool = document.createElement('div');
    pool.className = 'rr-pools';
    /** Обновляет барабаны и панель результата после ручной правки пула. */
    const refreshReels = (): void => {
        mapReel.refresh();
        modReel.refresh();
        drawPickPanel(false);
    };
    // Карты — сразу под рандомизатором (слева настройки пула, справа крупный список),
    // моды — ниже горизонтальной панелью.
    const setExcluded = (kind: Kind) => (ids: number[]): void => {
        excluded[kind] = ids;
        saveExcluded();
        refreshPresetsPanel();
    };
    const mapPool = buildPoolPicker({
        kind: 'maps',
        items: pools.maps,
        presets,
        getExcluded: () => excluded.maps,
        setExcluded: setExcluded('maps'),
        onChange: refreshReels,
        viewSwitch: true,
        skipUnbalanced: {
            get: () => skipUnbalanced,
            set: (value) => {
                skipUnbalanced = value;
                saveSkipUnbalanced();
                // Выпавшая карта могла попасть под пропуск — снимаем результат
                if (lastPick.maps != null) {
                    const picked = pools.maps.find((it) => it.id === lastPick.maps);
                    if (picked && isSkippedUnbalanced(picked)) lastPick.maps = null;
                }
            },
        },
    });
    const modPool = buildPoolPicker({
        kind: 'mods',
        items: pools.mods,
        presets,
        getExcluded: () => excluded.mods,
        setExcluded: setExcluded('mods'),
        onChange: refreshReels,
    });
    pool.append(mapPool.el);
    // Форма добавления карты — в карточке пула карт (как было раньше)
    if (canEditShared) mapPool.el.appendChild(buildAddMapForm());
    pool.append(modPool.el);

    const hint = document.createElement('div');
    hint.className = 'rr-hint';
    hint.textContent = t('tools.random_hint');

    // Слот под настройки выпавшего мода (заполняется после розыгрыша)
    const settingsBox = document.createElement('div');
    settingsBox.className = 'rr-settings-slot hidden';
    settingsSlot = settingsBox;

    // Порядок: розыгрыш → результат → настройки мода → пул карт → пул модов → пресеты в самом низу
    root.append(machine, pickPanel, settingsBox, hint, pool, buildPresetsPanel());
    drawSettingsResult();
    drawPickPanel(false);
}

/** Барабан: окно на 3 строки, в центре — выбранный вариант. */
function buildReel(label: string, kind: Kind): {
    el: HTMLElement;
    spin: (ms: number) => Promise<void>;
    refresh: () => void;
} {
    const el = document.createElement('div');
    el.className = 'rr-reel';

    const lab = document.createElement('div');
    lab.className = 'rr-reel-label';
    lab.textContent = label;

    const win = document.createElement('div');
    win.className = 'rr-window';
    const strip = document.createElement('div');
    strip.className = 'rr-strip';
    const pointer = document.createElement('div');
    pointer.className = 'rr-pointer';
    win.append(strip, pointer);
    el.append(lab, win);

    const cell = (text: string, title?: string): HTMLElement => {
        const d = document.createElement('div');
        d.className = 'rr-cell';
        // Текст во внутреннем span — по его ширине подгоняем размер шрифта
        const span = document.createElement('span');
        span.className = 'rr-cell-text';
        span.textContent = text;
        d.appendChild(span);
        if (title) d.title = title;
        return d;
    };

    /** Уменьшает шрифт ячеек, чьё название не влезает в ширину барабана. */
    const fitCells = (cells: Iterable<Element>): void => {
        for (const c of cells) {
            const d = c as HTMLElement;
            const span = d.firstElementChild as HTMLElement | null;
            if (!span) continue;
            d.style.fontSize = '';
            const cs = getComputedStyle(d);
            const avail = d.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
            const need = span.scrollWidth;
            if (avail <= 0 || need <= avail) continue;
            // Не мельче 55% от базового — дальше уже обрезка многоточием
            const k = Math.max(0.55, avail / need);
            d.style.fontSize = `${parseFloat(cs.fontSize) * k}px`;
        }
    };
    // Ширина окна могла измениться (ресайз, перенос барабанов в столбик) — перегоняем шрифты
    new ResizeObserver(() => fitCells(strip.children)).observe(win);
    const randomOf = (arr: PoolItem[]): PoolItem => arr[Math.floor(Math.random() * arr.length)]!;

    /** Статичное состояние: в центре последний результат или «?». */
    const refresh = (): void => {
        const items = enabledOf(kind);
        strip.style.transition = 'none';
        strip.style.transform = 'translateY(0)';
        strip.innerHTML = '';
        win.classList.toggle('is-empty', items.length === 0);
        win.classList.remove('is-hit');
        const last = pools[kind].find((it) => it.id === lastPick[kind]);
        const centerText = items.length === 0 ? t('tools.random_empty') : last ? last.name : '?';
        const side = () => (items.length ? randomOf(items).name : '');
        strip.append(cell(side()), cell(centerText, last?.title), cell(side()));
        if (last) strip.children[1]?.classList.add('is-hit');
        fitCells(strip.children);
    };

    const spin = (ms: number): Promise<void> => {
        const items = enabledOf(kind);
        if (items.length === 0) {
            refresh();
            return Promise.resolve();
        }

        // Цель: случайный вариант, не повторяющий прошлый (если есть из чего выбрать)
        const candidates = items.length > 1 ? items.filter((it) => it.id !== lastPick[kind]) : items;
        const target = randomOf(candidates);
        lastPick[kind] = target.id;

        // Лента: [сосед, текущий центр, ...наполнитель..., цель, сосед].
        // Начинаем с translateY(0) (центр = индекс 1) и едем до цели.
        const prevCenter = strip.children[1]?.textContent ?? '?';
        const fillers = Math.round(ms / 70);
        strip.style.transition = 'none';
        strip.style.transform = 'translateY(0)';
        strip.innerHTML = '';
        win.classList.remove('is-hit');
        strip.append(cell(randomOf(items).name), cell(prevCenter));
        for (let i = 0; i < fillers; i++) strip.appendChild(cell(randomOf(items).name));
        const targetCell = cell(target.name, target.title);
        strip.append(targetCell, cell(randomOf(items).name));
        const targetIdx = fillers + 2;
        fitCells(strip.children);

        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        const dur = reduced ? 300 : ms;

        win.classList.add('is-spinning');
        void strip.offsetHeight; // reflow, чтобы transition стартовал с 0
        strip.style.transition = `transform ${dur}ms cubic-bezier(0.12, 0.65, 0.12, 1)`;
        strip.style.transform = `translateY(${-(targetIdx - 1) * ITEM_H}px)`;

        return new Promise((resolve) => {
            setTimeout(() => {
                win.classList.remove('is-spinning');
                win.classList.add('is-hit');
                targetCell.classList.add('is-hit');
                fitCells([targetCell]); // у выпавшей шрифт крупнее — подгоняем заново
                resolve();
            }, dur + 30);
        });
    };

    refresh();
    return { el, spin, refresh };
}


// ============================================================
// Добавление карты (moderator/ghost/admin): POST /api/game-refs/maps
// ============================================================
function buildAddMapForm(): HTMLElement {
    const form = document.createElement('form');
    form.className = 'rr-add-map';

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'rr-input';
    input.maxLength = 100;
    input.placeholder = t('tools.map_add_placeholder');

    // Альт. название (локализация клиента) — необязательно, по нему тоже матчится реплей
    const altInput = document.createElement('input');
    altInput.type = 'text';
    altInput.className = 'rr-input';
    altInput.maxLength = 100;
    altInput.placeholder = t('tools.map_add_alt_placeholder');

    const btn = document.createElement('button');
    btn.type = 'submit';
    btn.className = 'btn-secondary';
    btn.textContent = t('tools.map_add');

    const msg = document.createElement('div');
    msg.className = 'rr-msg';

    form.append(input, altInput, btn, msg);
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const name = input.value.trim();
        if (!name) return;
        btn.disabled = true;
        msg.textContent = '';
        try {
            const altName = altInput.value.trim();
            const map = await createRef<GameMap>(
                'maps',
                altName ? { name, alt_name: altName } : { name },
                state.token
            );
            // Сервер может «воскресить» мягко удалённую карту — дубли не плодим
            if (!pools.maps.some((m) => m.id === map.id)) {
                pools.maps.push({
                    id: map.id,
                    name: map.name,
                    title: map.alt_name ?? undefined,
                    mapTypes: map.map_types ?? [],
                    teamFormats: mapTeamFormats(map),
                    playerCount: map.player_count ?? null,
                    imageUrl: map.image_url ?? null,
                    unbalanced: Boolean(map.unbalanced),
                });
            }
            excluded.maps = excluded.maps.filter((id) => id !== map.id);
            saveExcluded();
            rerender();
        } catch (err) {
            msg.textContent =
                err instanceof ApiRequestError && err.status === 409
                    ? t('tools.map_exists')
                    : `${t('tools.preset_error')} ${err instanceof Error ? err.message : String(err)}`;
            btn.disabled = false;
        }
    });
    return form;
}

// ============================================================
// Панель пресетов
// ============================================================
/** Включённые в пул id (для пресетов): пропуск дисбалансных не учитываем. */
const enabledIds = (kind: Kind): number[] =>
    pools[kind].filter((it) => !excluded[kind].includes(it.id)).map((it) => it.id);

/** Совпадает ли текущий выбор с пресетом (с учётом только существующих карт/модов). */
function presetMatches(p: MapPreset): boolean {
    const same = (kind: Kind, ids: number[]): boolean => {
        const want = new Set(ids.filter((id) => pools[kind].some((it) => it.id === id)));
        const cur = enabledIds(kind);
        return cur.length === want.size && cur.every((id) => want.has(id));
    };
    return same('maps', p.map_ids) && same('mods', p.mod_ids);
}

const canEdit = (p: MapPreset): boolean => (p.shared ? canEditShared : Boolean(state.user));
const presetLabel = (p: MapPreset): string => `${p.name} (${p.map_ids.length}/${p.mod_ids.length})`;

function applyPreset(p: MapPreset): void {
    const mapSet = new Set(p.map_ids);
    const modSet = new Set(p.mod_ids);
    excluded = {
        maps: pools.maps.filter((it) => !mapSet.has(it.id)).map((it) => it.id),
        mods: pools.mods.filter((it) => !modSet.has(it.id)).map((it) => it.id),
    };
    saveExcluded();
    setActivePreset(p.id);
    rerender();
}

/** Динамический пресет: ограничивает только карты, выбор модов не меняет. */
function applyDynamicPreset(preset: DynamicPreset): void {
    const keep = new Set(dynamicMapsOf(preset).map((it) => it.id));
    excluded = {
        maps: pools.maps.filter((it) => !keep.has(it.id)).map((it) => it.id),
        mods: excluded.mods,
    };
    saveExcluded();
    setActiveDynamic(preset);
    rerender();
}

function buildPresetsPanel(): HTMLElement {
    const box = document.createElement('div');
    box.className = 'card rr-presets';

    /** Раздел пропускают, пока им ни разу не пользовались — подсвечиваем до первого выбора. */
    const presetAck = (): boolean => {
        try {
            return localStorage.getItem(PRESET_ACK_KEY) === '1';
        } catch {
            return false;
        }
    };
    const needsAttention = (): boolean =>
        !presetAck() && presets.length > 0 && activePresetId === null && activeDynamic === null;

    // --- Заголовок: название, счётчик и пояснение, зачем раздел нужен ---
    const head = document.createElement('div');
    head.className = 'rr-presets-head';

    const title = document.createElement('div');
    title.className = 'rr-pool-title rr-presets-title';
    title.textContent = t('tools.preset_title');

    const count = document.createElement('span');
    count.className = 'rr-presets-count';
    if (presets.length > 0) {
        const shared = presets.filter((p) => p.shared).length;
        count.textContent = `${shared} ${t('tools.preset_shared').toLowerCase()} · ${
            presets.length - shared
        } ${t('tools.preset_personal').toLowerCase()}`;
    } else {
        count.classList.add('hidden');
    }
    head.append(title, count);

    const subtitle = document.createElement('div');
    subtitle.className = 'rr-presets-subtitle';
    subtitle.textContent =
        presets.length > 0 ? t('tools.preset_subtitle') : t('tools.preset_empty_hint');

    // --- Быстрый выбор чипами: заметнее, чем выпадающий список ---
    const quick = document.createElement('div');
    quick.className = 'rr-presets-quick';
    const hasLeagueMaps = dynamicMapsOf({ kind: 'league' }).length > 0;
    const quickFormats = presentTeamFormats();
    if (presets.length > 0 || hasLeagueMaps) {
        const quickLabel = document.createElement('span');
        quickLabel.className = 'rr-presets-quick-label';
        quickLabel.textContent = t('tools.preset_quick');
        quick.appendChild(quickLabel);

        for (const p of presets) {
            const chip = document.createElement('button');
            chip.type = 'button';
            // Точка перед названием (см. .rr-preset-pick::before) — общий пресет золотой
            chip.className = 'rr-preset-pick' + (p.shared ? ' rr-preset-pick--shared' : '');
            chip.dataset.presetId = String(p.id);
            chip.title = `${p.shared ? t('tools.preset_shared') : t('tools.preset_personal')} · ${
                p.map_ids.length
            } / ${p.mod_ids.length}`;
            chip.textContent = p.name;
            chip.addEventListener('click', () => applyPreset(p));
            quick.appendChild(chip);
        }

        // «Все карты FFA Лига» — динамический пресет одним кликом
        if (hasLeagueMaps) {
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'rr-preset-pick rr-preset-pick-dynamic rr-preset-pick--shared';
            chip.dataset.dynamic = LEAGUE_DYNAMIC_TYPE;
            chip.textContent = t('tools.preset_league_maps');
            chip.addEventListener('click', () => applyDynamicPreset({ kind: 'league' }));
            quick.appendChild(chip);
        }

        // Командные форматы (2 на 2, 4 на 4, …) в быстром выборе не показываем — только в списке пресетов
    } else {
        quick.classList.add('hidden');
    }

    // --- Выбор пресета: применяется сразу ---
    const row = document.createElement('div');
    row.className = 'rr-presets-row';

    const select = document.createElement('select');
    select.className = 'games-filter-select rr-presets-select';
    const cur = document.createElement('option');
    cur.value = '';
    cur.textContent = t('tools.preset_current');
    select.appendChild(cur);
    const group = (label: string, list: MapPreset[]): void => {
        if (list.length === 0) return;
        const og = document.createElement('optgroup');
        og.label = label;
        for (const p of list) {
            const o = document.createElement('option');
            o.value = String(p.id);
            o.textContent = presetLabel(p);
            og.appendChild(o);
        }
        select.appendChild(og);
    };
    group(t('tools.preset_shared'), presets.filter((p) => p.shared));
    group(t('tools.preset_personal'), presets.filter((p) => !p.shared));

    // Динамический пресет «Все карты FFA Лига»: объединение общих пресетов лиги
    if (hasLeagueMaps) {
        const og = document.createElement('optgroup');
        og.label = t('tools.match_preset_dynamic');
        const o = document.createElement('option');
        o.value = `d:${LEAGUE_DYNAMIC_TYPE}`;
        o.textContent = t('tools.preset_league_maps');
        og.appendChild(o);
        select.appendChild(og);
    }

    // Динамические пресеты по формату командной карты (2 на 2, 3 на 3, …)
    if (quickFormats.length > 0) {
        const og = document.createElement('optgroup');
        og.label = `${t('tools.match_preset_dynamic')} · ${t('tools.random_types_teamformat')}`;
        for (const format of quickFormats) {
            const o = document.createElement('option');
            o.value = `d:fmt:${format}`;
            o.textContent = teamFormatLabel(format);
            og.appendChild(o);
        }
        select.appendChild(og);
    }

    // Динамические пресеты по типу карты и числу игроков (по текущему пулу)
    for (const type of MAP_TYPES) {
        const maps = pools.maps.filter((it) => (it.mapTypes ?? []).includes(type));
        if (maps.length === 0) continue;
        const og = document.createElement('optgroup');
        og.label = `${t('tools.match_preset_dynamic')} · ${t(`map_type.${type}` as any)}`;

        const any = document.createElement('option');
        any.value = `d:${type}:all`;
        any.textContent = t('tools.match_preset_any_count');
        og.appendChild(any);

        const counts = [
            ...new Set(maps.map((it) => it.playerCount).filter((n): n is number => n != null)),
        ].sort((a, b) => a - b);
        for (const c of counts) {
            const o = document.createElement('option');
            o.value = `d:${type}:${c}`;
            o.textContent = t('tools.random_players', { n: c });
            og.appendChild(o);
        }
        select.appendChild(og);
    }

    select.value = activePresetId !== null
        ? String(activePresetId)
        : activeDynamic
            ? dynamicValue(activeDynamic)
            : '';

    const modified = document.createElement('span');
    modified.className = 'rr-presets-modified';
    modified.textContent = t('tools.preset_modified');

    // «изменён» остаётся рядом с выбором, кнопки правки — в отдельном блоке ниже
    row.append(select, modified);

    // --- Блок редактирования: отделён линией и подписан, чтобы не путался с выбором ---
    const editor = document.createElement('div');
    editor.className = 'rr-presets-editor';

    const editorHead = document.createElement('div');
    editorHead.className = 'rr-presets-editor-head';
    const editorTitle = document.createElement('div');
    editorTitle.className = 'rr-presets-editor-title';
    editorTitle.textContent = t('tools.preset_editor_title');
    const editorHint = document.createElement('div');
    editorHint.className = 'rr-presets-editor-hint';
    editorHint.textContent = t('tools.preset_editor_hint');
    editorHead.append(editorTitle, editorHint);

    // Перезапись/удаление выбранного пресета (показываются, когда есть что менять)
    const manageRow = document.createElement('div');
    manageRow.className = 'rr-presets-row rr-presets-manage';

    const updateBtn = document.createElement('button');
    updateBtn.type = 'button';
    updateBtn.className = 'btn-secondary';
    updateBtn.textContent = t('tools.preset_update');

    // Сброс правок к сохранённому состоянию пресета (без записи в БД)
    const resetBtn = document.createElement('button');
    resetBtn.type = 'button';
    resetBtn.className = 'btn-secondary';
    resetBtn.textContent = t('tools.preset_reset');

    const deleteBtn = document.createElement('button');
    deleteBtn.type = 'button';
    deleteBtn.className = 'btn-secondary rr-danger';
    deleteBtn.textContent = t('tools.preset_delete');

    manageRow.append(updateBtn, resetBtn, deleteBtn);
    editor.append(editorHead, manageRow);

    // --- Сохранение текущего выбора как нового пресета ---
    const saveRow = document.createElement('form');
    saveRow.className = 'rr-presets-row';
    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.className = 'rr-input';
    nameInput.maxLength = 60;
    nameInput.placeholder = t('tools.preset_name');
    const scope = document.createElement('select');
    scope.className = 'games-filter-select rr-presets-scope';
    for (const [value, key] of [
        ['personal', 'tools.preset_scope_personal'],
        ['shared', 'tools.preset_scope_shared'],
    ] as const) {
        const o = document.createElement('option');
        o.value = value;
        o.textContent = t(key);
        scope.appendChild(o);
    }
    const saveBtn = document.createElement('button');
    saveBtn.type = 'submit';
    saveBtn.className = 'btn-primary';
    saveBtn.textContent = t('tools.preset_save_new');
    saveRow.append(nameInput);
    if (canEditShared) saveRow.appendChild(scope);
    saveRow.appendChild(saveBtn);

    const nudge = document.createElement('div');
    nudge.className = 'rr-presets-nudge';
    nudge.textContent = t('tools.preset_attention');

    const msg = document.createElement('div');
    msg.className = 'rr-msg';

    const showError = (err: unknown): void => {
        msg.classList.remove('is-ok');
        msg.textContent =
            err instanceof ApiRequestError && err.status === 409
                ? t('tools.preset_exists')
                : `${t('tools.preset_error')} ${err instanceof Error ? err.message : String(err)}`;
    };

    /** Видимость кнопок, пометки «изменён» и подсветка раздела по текущему выбору. */
    const refresh = (): void => {
        const p = presets.find((x) => x.id === activePresetId);
        const dirty = !!p && !presetMatches(p);
        const canUpdate = !!p && dirty && canEdit(p);
        const canDelete = !!p && canEdit(p);
        modified.classList.toggle('hidden', !dirty);
        updateBtn.classList.toggle('hidden', !canUpdate);
        resetBtn.classList.toggle('hidden', !canUpdate);
        deleteBtn.classList.toggle('hidden', !canDelete);
        // Пустой блок правки не показываем
        manageRow.classList.toggle('hidden', !canUpdate && !canDelete);

        // Активный чип + подсветка-подсказка, пока пресет не выбран ни разу
        quick.querySelectorAll<HTMLElement>('.rr-preset-pick').forEach((chip) => {
            const active = chip.dataset.dynamic
                ? activeDynamic?.kind === 'league'
                : chip.dataset.format
                    ? activeDynamic?.kind === 'format' && activeDynamic.format === chip.dataset.format
                    : Number(chip.dataset.presetId) === activePresetId;
            chip.classList.toggle('is-active', active);
        });
        if (activePresetId !== null || activeDynamic) {
            try {
                localStorage.setItem(PRESET_ACK_KEY, '1');
            } catch {
                /* без localStorage */
            }
        }
        const attention = needsAttention();
        box.classList.toggle('is-attention', attention);
        nudge.classList.toggle('hidden', !attention);
    };
    refreshPresetsPanel = () => {
        msg.textContent = '';
        refresh();
    };
    refresh();

    select.addEventListener('change', () => {
        const value = select.value;
        if (value.startsWith('d:')) {
            const preset = parseDynamicValue(value);
            if (preset) applyDynamicPreset(preset);
            return;
        }
        const p = presets.find((x) => String(x.id) === value);
        if (p) {
            applyPreset(p);
        } else {
            setActivePreset(null);
            refreshPresetsPanel();
        }
    });

    saveRow.addEventListener('submit', async (e) => {
        e.preventDefault();
        const name = nameInput.value.trim();
        if (!name) return;
        saveBtn.disabled = true;
        try {
            const res = await apiRequest<{ preset: MapPreset }>('/api/map-presets', {
                method: 'POST',
                token: state.token,
                body: {
                    name,
                    shared: canEditShared && scope.value === 'shared',
                    map_ids: enabledIds('maps'),
                    mod_ids: enabledIds('mods'),
                },
            });
            presets.push(res.preset);
            presets.sort((a, b) => a.name.localeCompare(b.name));
            setActivePreset(res.preset.id);
            rerender();
        } catch (err) {
            showError(err);
            saveBtn.disabled = false;
        }
    });

    // Сброс правок: возвращаем пул к сохранённым на сервере картам/модам
    resetBtn.addEventListener('click', () => {
        const p = presets.find((x) => x.id === activePresetId);
        if (!p) return;
        applyPreset(p);
    });

    updateBtn.addEventListener('click', async () => {
        const p = presets.find((x) => x.id === activePresetId);
        if (!p) return;
        updateBtn.disabled = true;
        try {
            const res = await apiRequest<{ preset: MapPreset }>(`/api/map-presets/${p.id}`, {
                method: 'PATCH',
                token: state.token,
                body: { map_ids: enabledIds('maps'), mod_ids: enabledIds('mods') },
            });
            Object.assign(p, res.preset);
            const opt = select.querySelector<HTMLOptionElement>(`option[value="${p.id}"]`);
            if (opt) opt.textContent = presetLabel(p);
            refresh();
            msg.classList.add('is-ok');
            msg.textContent = t('tools.preset_saved');
        } catch (err) {
            showError(err);
        } finally {
            updateBtn.disabled = false;
        }
    });

    deleteBtn.addEventListener('click', async () => {
        const p = presets.find((x) => x.id === activePresetId);
        if (!p || !confirm(t('tools.preset_delete_confirm', { name: p.name }))) return;
        deleteBtn.disabled = true;
        try {
            await apiRequest(`/api/map-presets/${p.id}`, { method: 'DELETE', token: state.token });
            presets = presets.filter((x) => x.id !== p.id);
            setActivePreset(null);
            rerender();
        } catch (err) {
            showError(err);
            deleteBtn.disabled = false;
        }
    });

    box.append(head, subtitle, quick, row, nudge, editor);
    if (state.user) {
        editor.appendChild(saveRow);
    } else {
        // Гостям сохранение недоступно — подсказка живёт в том же блоке
        const login = document.createElement('div');
        login.className = 'rr-hint';
        login.textContent = t('tools.preset_login_hint');
        editor.appendChild(login);
        editor.classList.add('is-locked');
    }
    editor.appendChild(msg);
    return box;
}
